import React from 'react';

import { alpha, decomposeColor, lighten, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { TagType } from 'constants/tag';
import { COLOR_PALETTE } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

import { FONT_WEIGHT_BOLD, FONT_WEIGHT_NORMAL } from './Tag.constants';

const DARK_BACKGROUND_ALPHA = 0.2;
const DARK_TEXT_LIGHTEN = 0.4;
const PARSEABLE_COLOR_REGEX = /^#|^rgb|^hsl/i;

// Status tags receive light-mode values (a hue fill + dark text); on dark the hue would leak
// near-white or, when the hue lives in backgroundColor and the text is the default dark gray, the
// chip would collapse to one dim gray that loses its colour-coding. Derive a hue-preserving dark
// recipe from whichever source actually carries the hue: a low-alpha tint of it as the fill + a
// lightened version of it as readable text.
const getDarkBackgroundColor = color => alpha(color, DARK_BACKGROUND_ALPHA);
const getDarkColor = color => lighten(color, DARK_TEXT_LIGHTEN);

const getChroma = value => {
  const [r, g, b] = decomposeColor(value).values;

  return Math.max(r, g, b) - Math.min(r, g, b);
};

// backgroundColor is usually a TagColor key (e.g. 'yellow') resolved to a hex via theme.tag; the
// hue is the more chromatic of the resolved fill and the text colour, so an archived gray (hue in
// color) and a coloured status (hue in backgroundColor) both keep their intended dark appearance.
const resolveDarkHue = ({ tag, backgroundColor, color }) => {
  const resolvedBackground = tag?.[backgroundColor] ?? backgroundColor;
  const isBackgroundParseable = PARSEABLE_COLOR_REGEX.test(resolvedBackground);

  return isBackgroundParseable && getChroma(resolvedBackground) >= getChroma(color)
    ? resolvedBackground
    : color;
};

const getTagStyles = ({ color, backgroundColor, borderRadius, type }) => ({
  display: 'inline-block',
  width: 'fit-content',
  maxWidth: '100%',
  height: 'fit-content',
  backgroundColor: ({ palette, tag }) =>
    palette.mode === 'dark'
      ? getDarkBackgroundColor(resolveDarkHue({ tag, backgroundColor, color }))
      : backgroundColor,
  color: ({ palette, tag }) =>
    palette.mode === 'dark' ? getDarkColor(resolveDarkHue({ tag, backgroundColor, color })) : color,

  ...getConditionalObject([TagType.SMALL, TagType.SMALL_FAT, TagType.MEDIUM].includes(type), {
    padding: type === TagType.MEDIUM ? '0 8px' : '0 5px',
    fontSize: '12px',
    lineHeight: '22px',
    borderRadius: type === TagType.SMALL_FAT ? '6px' : `${borderRadius}px`,
    maxWidth: '160px',
    fontWeight: type === TagType.SMALL_FAT ? FONT_WEIGHT_BOLD : FONT_WEIGHT_NORMAL,
  }),

  ...getConditionalObject(type === TagType.LARGE, {
    padding: '5px 10px',
    fontSize: '14px',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    borderRadius: `${borderRadius}px`,
  }),
});

const Tag = ({
  borderRadius = 12,
  color = COLOR_PALETTE.GRAY[10],
  backgroundColor,
  text,
  dataTestId,
  className,
  dataTruncated = false,
  type = TagType.LARGE,
  ...props
}) => {
  const tagStyles = getTagStyles({ color, backgroundColor, borderRadius, type });

  return (
    <Typography
      className={className}
      data-testid={dataTestId}
      data-truncated={dataTruncated}
      sx={tagStyles}
      {...props}
      variant="regular14"
    >
      {text}
    </Typography>
  );
};

Tag.propTypes = {
  backgroundColor: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  borderRadius: PropTypes.number,
  color: PropTypes.string,
  dataTestId: PropTypes.string,
  dataTruncated: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(TagType)),
};

export default Tag;
